import {ChronoUnit, Duration, LocalDateTime} from '@js-joda/core';
import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import Hidden from '@material-ui/core/Hidden';
import IconButton from '@material-ui/core/IconButton';
import {alpha} from '@material-ui/core/styles';
import makeStyles from '@material-ui/core/styles/makeStyles';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ConfirmIcon from '@material-ui/icons/Check';
import CancelIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import DoneIcon from '@material-ui/icons/Done';
import EditIcon from '@material-ui/icons/Edit';
import ErrorIcon from '@material-ui/icons/Error';
import CopyIcon from '@material-ui/icons/FileCopy';
import LockIcon from '@material-ui/icons/Lock';
import TimerIcon from '@material-ui/icons/Timer';
import {usePreferencesState} from '@tracker/shared/src/Preferences/PreferencesProvider';
import React, {ReactNode, useCallback, useState} from 'react';
import {TimeEntry} from '../../pages/TimeEntries';
import MessageDialog from './MessageDialog';
import TimeChip from './TimeChip';
import TimeEntryFormDialog from './TimeEntryFormDialog';
import {getMinutes} from './TimeEntryTable';
import UnSubmitDialog from "./UnSubmitDialog";

const useStyles = makeStyles(theme => ({
    note: {
        fontSize: '0.8rem',
        color: alpha(theme.palette.text.primary, 0.65),
    },
    billableHoursCell: {
        paddingRight: 0,
    },
    timerStoppingIndicator: {
        verticalAlign: 'middle',
    },
    deletion: {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
        textAlign: 'center',
    },
    incomplete: {
        backgroundColor: theme.palette.warning[theme.palette.type],
        color: theme.palette.warning.contrastText,
    },
    error: {
        backgroundColor: theme.palette.error.light,
        color: theme.palette.error.contrastText,
    },
    actionCell: {
        [theme.breakpoints.up('md')]: {
            minWidth: 176,
        },
    },
    ticketDescription: {
        float: 'left'
    },
    ticketError: {
        float: 'right'
    },
}));

type Props = {
    timeEntry : TimeEntry;
    onInsert : (timeEntry : TimeEntry) => Promise<void>;
    onUpdate : (timeEntry : TimeEntry) => Promise<void>;
    onDelete : (timeEntry : TimeEntry) => Promise<boolean>;
    onUnsubmit : (timeEntry : TimeEntry) => Promise<void>;
};

const formatTimeEntry = (showProjectAndComponent : boolean, showTaskId : boolean, timeEntry : TimeEntry) : string => {
    let entryText = (showProjectAndComponent && timeEntry?.project?.name ? timeEntry.project.name : '');
    if (showProjectAndComponent && timeEntry.component) {
        entryText += `: ${timeEntry.component.name}: `;
    }

    if (timeEntry.task) {
        entryText += timeEntry.task.name + (showTaskId ? `: ${timeEntry.task?.id}` : '')
    }

    return entryText;
}

const TimeEntryRow : React.FC<Props> = ({timeEntry, onInsert, onUpdate, onDelete, onUnsubmit} : Props) => {
    const classes = useStyles();
    const [stoppingTimer, setStoppingTimer] = useState(false);
    const [confirmDeletion, setConfirmDeletion] = useState(false);
    const [formInitialValues, setFormInitialValues] = useState<Partial<TimeEntry> | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [showUnsubmit, setShowUnsubmit] = useState<boolean>(false);
    const {i18n, preferences} = usePreferencesState();

    const stopTimer = useCallback(async () => {
        if (!timeEntry.startTime) {
            return;
        }

        setStoppingTimer(true);

        const now = LocalDateTime.now();
        const startDateTime = timeEntry.startDate.atTime(timeEntry.startTime);
        let totalMinutes = Duration.between(startDateTime, now).toMinutes();
        const {roundToNearest} = preferences.timeEntry;

        if (totalMinutes < roundToNearest) {
            totalMinutes = roundToNearest;
        } else {
            totalMinutes = Math.round(totalMinutes / roundToNearest) * roundToNearest;
        }
        const endTime = startDateTime.plusMinutes(totalMinutes).toLocalTime();

        await onUpdate({
            ...timeEntry,
            endTime
        });
        setStoppingTimer(false);
    }, [timeEntry, preferences, setStoppingTimer, onUpdate]);

    const handleDuplicate = useCallback(() => {
        const initialValues : Partial<TimeEntry> = {};
        initialValues.task = timeEntry.task;
        initialValues.note = timeEntry.note;
        initialValues.startDate = timeEntry.startDate;
        initialValues.isPMTimeEntry = timeEntry.isPMTimeEntry;

        if (preferences.timeEntry.keepTimesOnDuplicate) {
            initialValues.startTime = timeEntry.startTime;
            initialValues.minutes = timeEntry.minutes;
        }

        setFormInitialValues(initialValues);
    }, [preferences, setFormInitialValues, timeEntry]);

    const handelUnsubmit = useCallback(async () => {
        setShowUnsubmit(false);
        onUnsubmit(timeEntry);
    }, [onUnsubmit, timeEntry]);

    const deleteTimeEntry = useCallback(async () => {
        setDeleting(true);
        if (!await onDelete(timeEntry)) {
            setDeleting(false);
            setConfirmDeletion(false);
        }
    }, [timeEntry, onDelete]);

    const handleSubmit = useCallback(async (timeEntry) => {
        setFormInitialValues(null);
        return timeEntry.id ? onUpdate(timeEntry) : onInsert(timeEntry);
    }, [setFormInitialValues, onInsert, onUpdate]);

    const [messageOpen, setMessageOpen] = React.useState(false);

    let startTimeText = '';
    let endTimeText = '';

    if (timeEntry.startTime) {
        startTimeText = timeEntry.startTime.format(i18n.timeFormatter);

        if (timeEntry.minutes !== null) {
            const startDateTime = timeEntry.startDate.atTime(timeEntry.startTime);
            const endDateTime = startDateTime.plusMinutes(timeEntry.minutes);
            endTimeText = endDateTime.format(i18n.timeFormatter);

            const dayDiff = startDateTime.until(endDateTime, ChronoUnit.DAYS);

            if (dayDiff > 0) {
                endTimeText += ` +${dayDiff}D`;
            }
        }
    }

    let hoursCells : ReactNode;

    if (timeEntry.minutes !== null) {
        const time = getMinutes(timeEntry);

        hoursCells = (
            <>
                <TableCell className={classes.billableHoursCell}>
                    {time.billable > 0 && <TimeChip minutes={time.billable} billable={true}/>}
                </TableCell>
                <TableCell><TimeChip minutes={time.total} billable={false}/></TableCell>
            </>
        );
    } else if (timeEntry.startTime) {
        if (stoppingTimer) {
            hoursCells = (
                <TableCell colSpan={2}>
                    <CircularProgress size={25} className={classes.timerStoppingIndicator}/>
                </TableCell>
            );
        } else {
            hoursCells = (
                <TableCell colSpan={2}>
                    <Tooltip title="Stop timer">
                        <IconButton edge="start" onClick={stopTimer}><TimerIcon/></IconButton>
                    </Tooltip>
                </TableCell>
            );
        }
    } else {
        hoursCells = <TableCell colSpan={2}/>;
    }

    if (deleting) {
        return (
            <TableRow>
                <TableCell colSpan={6} className={classes.deletion}>
                    <CircularProgress size={25} className={classes.timerStoppingIndicator}/>
                </TableCell>
            </TableRow>
        );
    }

    const incomplete = !timeEntry.project || timeEntry.minutes === null;
    const isError = timeEntry.error && ! timeEntry.submitted;
    const className = isError ? classes.error : (incomplete ? classes.incomplete : undefined);
    const entryText = formatTimeEntry(preferences.timeEntry.showProjectAndComponent, preferences.timeEntry.showTaskId, timeEntry);

    return (
        <TableRow className={className}>
            <TableCell onClick={() => isError && setMessageOpen(true)}>
                <Box className={classes.ticketDescription}>
                    {(preferences.timeEntry.showProjectAndComponent || timeEntry.task) && (
                        <Typography variant="body2">
                            {entryText}
                        </Typography>
                    )}
                    <Typography variant="body2" className={classes.note}>
                        {timeEntry.note}
                    </Typography>
                </Box>
            </TableCell>
            <Hidden smDown>
                <TableCell>
                    {isError && <Tooltip title={timeEntry.error}>
                        <IconButton onClick={() => setMessageOpen(true)}><ErrorIcon/></IconButton>
                    </Tooltip>}
                </TableCell>
                <TableCell>{startTimeText}</TableCell>
                <TableCell>{endTimeText}</TableCell>
            </Hidden>
            {hoursCells}
            <TableCell className={classes.actionCell}>
                {timeEntry.submitted ? <>
                    <Tooltip title="Duplicate">
                        <IconButton onClick={handleDuplicate}><CopyIcon/></IconButton>
                    </Tooltip>
                    <Tooltip title="Submitted">
                        <IconButton onClick={()=>setShowUnsubmit(true)}><DoneIcon/></IconButton>
                    </Tooltip>
                    {timeEntry.invoiced && <Tooltip title="Invoiced">
                        <IconButton><LockIcon/></IconButton>
                    </Tooltip> }
                    <UnSubmitDialog
                        open={showUnsubmit}
                        onClose={() => setShowUnsubmit(false)}
                        description={entryText}
                        startTime={startTimeText}
                        handelUnsubmit={handelUnsubmit}
                        isInvoiced={timeEntry.invoiced}
                    />
                </>: confirmDeletion ? (
                    <>
                        <Tooltip title="Confirm deletion">
                            <IconButton color="primary" onClick={deleteTimeEntry}><ConfirmIcon/></IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                            <IconButton onClick={() => setConfirmDeletion(false)}><CancelIcon/></IconButton>
                        </Tooltip>
                    </>
                ) : (
                    <>
                        <Tooltip title="Edit">
                            <IconButton onClick={() => setFormInitialValues(timeEntry)}><EditIcon/></IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate">
                            <IconButton onClick={handleDuplicate}><CopyIcon/></IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                            <IconButton onClick={() => setConfirmDeletion(true)}><DeleteIcon/></IconButton>
                        </Tooltip>
                    </>
                )}

                <TimeEntryFormDialog
                    timeEntry={formInitialValues || undefined}
                    open={formInitialValues !== null}
                    onClose={() => setFormInitialValues(null)}
                    onSubmit={handleSubmit}
                />
                <MessageDialog
                    open={messageOpen}
                    message={timeEntry.error}
                    onClose={() => setMessageOpen(false)}
                />
            </TableCell>
        </TableRow>
    );
};

export default TimeEntryRow;
